import type {
  RpiLatestResponse,
  PostsResponse,
  PostShowResponse,
  TeamShowResponse,
  SchoolShowResponse,
  NewsResponse,
  NewsShowResponse,
  CoachesPollResponse,
  TransfersResponse,
  RecruitsResponse,
  HighlightsResponse,
  DatatableResponse,
  GameRow,
  ScoreboardResponse,
} from '@/types/api'
import { toIsoDate } from '@/lib/dateUtils'

const BASE = '/api/v1'

// Carries the HTTP status so callers can distinguish a genuine 404 (resource
// missing) from a 5xx (server error) instead of collapsing both into one
// "not found" message.
export class ApiError extends Error {
  status: number

  constructor(status: number) {
    super(`API error: ${status}`)
    this.name = 'ApiError'
    this.status = status
  }
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { headers: { Accept: 'application/json' } })
  if (!res.ok) throw new ApiError(res.status)
  return res.json()
}

export function apiUrl(sport: string, path: string) {
  return `${BASE}/${sport}${path}`
}

export function fetchRpiLatest(sport: string) {
  return fetchJson<RpiLatestResponse>(apiUrl(sport, '/rpi/latest'))
}

export function fetchPosts(sport: string, category?: string) {
  const q = category ? `?category=${category}` : ''
  return fetchJson<PostsResponse>(apiUrl(sport, `/posts${q}`))
}

export function fetchPost(sport: string, slug: string) {
  return fetchJson<PostShowResponse>(apiUrl(sport, `/posts/${slug}`))
}

export function fetchTeam(sport: string, slug: string) {
  return fetchJson<TeamShowResponse>(apiUrl(sport, `/teams/${slug}`))
}

export function fetchSchool(slug: string) {
  // Not sport-scoped — a school aggregates its teams across sports.
  return fetchJson<SchoolShowResponse>(`${BASE}/schools/${slug}`)
}

export function fetchNews(sport: string, source?: string) {
  const q = source ? `?source=${source}` : ''
  return fetchJson<NewsResponse>(apiUrl(sport, `/news${q}`))
}

export function fetchNewsArticle(sport: string, slug: string) {
  return fetchJson<NewsShowResponse>(apiUrl(sport, `/news/${slug}`))
}

export function fetchCoachesPollLatest(sport: string) {
  return fetchJson<CoachesPollResponse>(apiUrl(sport, '/coaches_poll/latest'))
}

export function fetchTransfers(sport: string, opts?: { status?: string; conference?: string; q?: string }) {
  const params = new URLSearchParams()
  if (opts?.status) params.set('status', opts.status)
  if (opts?.conference) params.set('conference', opts.conference)
  if (opts?.q) params.set('q', opts.q)
  const q = params.toString() ? `?${params}` : ''
  return fetchJson<TransfersResponse>(apiUrl(sport, `/transfers${q}`))
}

export function fetchRecruits(sport: string, opts?: { class_year?: number; school?: string; position?: string; ranked?: boolean }) {
  const params = new URLSearchParams()
  if (opts?.class_year) params.set('class_year', String(opts.class_year))
  if (opts?.school) params.set('school', opts.school)
  if (opts?.position) params.set('position', opts.position)
  if (opts?.ranked) params.set('ranked', 'true')
  const q = params.toString() ? `?${params}` : ''
  return fetchJson<RecruitsResponse>(apiUrl(sport, `/recruits${q}`))
}

export function fetchHighlights(sport: string) {
  return fetchJson<HighlightsResponse>(apiUrl(sport, '/highlights'))
}

export function datatableUrl(sport: string, table: string) {
  return apiUrl(sport, `/datatables/${table}.json`)
}

// One populated day of the scoreboard feed. Omit `before` for the anchor day
// (today, or last night in the morning) plus the featured strip; pass the
// previous response's `next_cursor` (a YYYY-MM-DD date) to page backward in time.
export function fetchScoreboard(sport: string, before?: string | null) {
  const q = before ? `?before=${encodeURIComponent(before)}` : ''
  return fetchJson<ScoreboardResponse>(apiUrl(sport, `/scoreboard.json${q}`))
}

export function fetchRecentScores(sport: string, limit = 10, days = 1) {
  // Keep the homepage ticker timely: only today and the previous `days` day(s),
  // newest first. The datatable's date_range makes the end date inclusive of
  // the whole day, so today's games (including live/scheduled) are covered.
  const to = new Date()
  const from = new Date()
  from.setDate(to.getDate() - days)

  const params = new URLSearchParams({
    draw: '1',
    start: '0',
    length: String(limit),
    'order[0][column]': '0',
    'order[0][dir]': 'desc',
    ranked_only: 'false',
    'columns[0][data]': 'starts_at',
    'columns[0][searchable]': 'true',
    'columns[0][search][value]': `${toIsoDate(from)}~${toIsoDate(to)}`,
    'columns[0][search][regex]': 'false',
  })
  return fetchJson<DatatableResponse<GameRow>>(`${apiUrl(sport, '/datatables/games.json')}?${params}`)
}
